import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import find from "lodash/find";

import UnlockModal from "app/components/React/UnlockModalProvider/UnlockModalProvider";
import { swSettings } from "app/common/services/swSettings";
import { CountryFilter } from "app/components/filters-bar/country-filter/CountryFilter";
import * as utils from "app/components/filters-bar/utils";
import { WebSourceFilter } from "app/components/filters-bar/websource-filter/WebSourceFilter";
import type { IFilter } from "app/components/React/FiltersBar/FiltersBar";
import { FiltersBar } from "app/components/React/FiltersBar/FiltersBar";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";
import type { IUnlockModalConfigTypes } from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import unlockModalConfig from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { updateFilterConsistencyCountry } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { EHooksVersions } from "app/services/SwHook";
import { SwTrack } from "app/services/SwTrack";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";

import { KeywordGeneratorToolDurationFilter } from "./KeywordGeneratorToolDurationFilter";

export const KeywordGeneratorToolSubnavFilters = () => {
    const { params, state } = useCurrentStateAndParams();
    const { isUSStatesSupported } = state;
    const { country, webSource } = params;
    const [isWebSourceHookOpen, setIsWebSourceHookOpen] = useState<boolean>(false);

    const availableWebsource = utils.getAvailableWebSource(state, params);
    const selectedWebSource = find(availableWebsource, { id: webSource });

    const availableCountries = utils.getCountries(false, swSettings.current);
    const selectedCountry = find<{ id: number }>(availableCountries, {
        id: parseInt(country),
    });

    const trackFilterValue = (eventName: string) => {
        SwTrack.all.dispatchTrackEvent("Drop Down", "click", eventName);
    };

    const trackFilterDropDownStatus = (eventName: string) => {
        return (isOpen: boolean) => {
            SwTrack.all.dispatchTrackEvent("Drop down", isOpen ? "open" : "close", eventName);
        };
    };

    const onCountryChange = (country) => {
        if (!country) {
            return;
        }
        trackFilterValue(`Header/Country Filter/${country.text}`);

        updateFilterConsistencyCountry(country.id);
        proNavigator.updateParams({
            country: country.id,
            isCountryChanged: null,
        });
    };

    const onWebSourceChange = (webSource) => {
        const currentWebSource = proNavigator.getParams().webSource;
        if (currentWebSource === webSource.id) {
            return;
        }
        trackFilterValue(`Header/WebSource Filter/${webSource.text}`);
        proNavigator.updateQueryParams(
            {
                webSource: webSource.id,
                isWebSourceChanged: null,
            },
            {},
        );
    };

    const getCountryFilterComponent = (selectedCountryId) => {
        const isCountryChanged = !!proNavigator.getParams()?.isCountryChanged;
        return (
            <div key="filter-country">
                <CountryFilter
                    presentNotificationIndicator={isCountryChanged}
                    presentNotificationTooltipKey={"website.analysis.present.country.notification.tooltip"}
                    isUSStatesSupported={isUSStatesSupported}
                    key="filter-country"
                    appendTo="body"
                    availableCountries={availableCountries}
                    changeCountry={onCountryChange}
                    selectedCountryIds={selectedCountryId}
                    disabled={false}
                    showUpgradeHook={isNoTouch()}
                    height={60}
                    onToggle={trackFilterDropDownStatus("Header/Country Filter")}
                    itemWrapper={(props) => (
                        <div
                            onClick={() => {
                                handleCTAClick(EHooksVersions.V5, "WebCountry", TestCases.TRIAL_AFTER_TRIAL);
                            }}
                        >
                            {props.children}
                        </div>
                    )}
                />
            </div>
        );
    };

    const getWebSourceFilterComponent = (selectedWebSourceId) => {
        const activeSlide: IUnlockModalConfigTypes["DeviceFilters"] = "Devices";
        const isWebSourceChanged = !!proNavigator.getParams()?.isWebSourceChanged;
        return (
            <div key="filter-websource">
                <WebSourceFilter
                    appendTo="body"
                    items={availableWebsource}
                    onChange={onWebSourceChange}
                    selectedIds={selectedWebSourceId}
                    height={60}
                    onToggle={trackFilterDropDownStatus("Header/WebSource Filter")}
                    disabled={availableWebsource.length === 1}
                    presentNotificationIndicator={isWebSourceChanged}
                    presentNotificationTooltipKey={"website.analysis.present.webSource.notification.tooltip"}
                    itemWrapper={(props) => (
                        <div
                            onClick={() => {
                                setIsWebSourceHookOpen(true);
                                SwTrack.all.dispatchTrackEvent(
                                    "hook/Contact Us/Pop Up",
                                    "click",
                                    `WebSource Filter/${props.text}`,
                                );
                            }}
                        >
                            {props.children}
                        </div>
                    )}
                />
                <UnlockModal
                    isOpen={isWebSourceHookOpen}
                    onCloseClick={() => {
                        setIsWebSourceHookOpen(false);
                    }}
                    activeSlide={activeSlide}
                    location="Hook PRO/Website Analysis/Web Source Filter"
                    {...unlockModalConfig().DeviceFilters}
                />
            </div>
        );
    };

    const getFilters = (): IFilter[] => {
        const selectedCountryId = utils.getSelectedId(selectedCountry);
        const selectedWebSourceId = utils.getSelectedId(selectedWebSource);

        const filters = [
            {
                id: "duration",
                key: "duration",
                filter: <KeywordGeneratorToolDurationFilter />,
            },
            {
                id: "country",
                key: "country",
                filter: getCountryFilterComponent(selectedCountryId),
            },
        ];
        if (!!availableWebsource.length && selectedWebSourceId) {
            filters.push({
                id: "webSource",
                key: "webSource",
                filter: getWebSourceFilterComponent(selectedWebSourceId),
            });
        }
        return filters;
    };

    return (
        <div className="react-filters-container">
            <FiltersBar filters={getFilters()} />
        </div>
    );
};